import {
  OpenCopilotChatEvent,
  publishOpenCopilotChat,
  subscribeOpenCopilotChat,
  subscribePreloadCopilotChat,
} from '@github-ui/copilot-chat/utils/copilot-chat-events'
import {CopilotChatIntents} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {GlobalCopilotMenu} from '@github-ui/global-copilot-menu/GlobalCopilotMenu'
import {CopilotIcon} from '@primer/octicons-react'
import {ButtonGroup} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useState} from 'react'

import {NavLinkItem} from '../../primitives/NavItemLink'
import baseStyles from '../../primitives/styles.module.css'
import {useTopNavContext} from '../../primitives/TopNav'
import {useGlobalNavAnalytics} from '../../utils/Analytics'
import {LazyGlobalNavAgentsMenu} from '../LazyGlobalNavAgentsMenu'
import {CopilotChatLoader} from './CopilotChatLoader'
import styles from './CopilotItems.module.css'

export type CopilotItemsProps = {
  show?: boolean
  showAgentsButton?: boolean
  copilotApiUrl?: string | undefined
  copilotChatUrl?: string
}

export function CopilotItems({show, showAgentsButton, copilotApiUrl, copilotChatUrl}: CopilotItemsProps) {
  const {shouldLoadCopilotChat, loadCopilotChat} = useTopNavContext()
  const {sendItemClick} = useGlobalNavAnalytics('COPILOT')
  const isImmersiveEmbeddedHeaderButton = isFeatureEnabled('copilot_immersive_embedded_header_button')
  const isDeferredPayload =
    isImmersiveEmbeddedHeaderButton && isFeatureEnabled('copilot_immersive_embedded_deferred_payload')

  // Initialize the anchor ref from the Copilot NavLink anchor element
  const [anchorReady, setAnchorReady] = useState(false)
  const anchorRefCallback = useCallback((node: HTMLAnchorElement | null) => {
    setAnchorReady(node !== null)
  }, [])
  // Store pending open event to re-dispatch after embedded component loads
  const [pendingOpenEvent, setPendingOpenEvent] = useState<OpenCopilotChatEvent | null>(null)

  // Subscribe to open-copilot-chat events to trigger loading.
  useEffect(() => {
    return subscribeOpenCopilotChat(e => {
      loadCopilotChat()
      // Store the event so we can re-dispatch it after the embedded component loads
      setPendingOpenEvent(e)
    })
  }, [loadCopilotChat])

  // Subscribe to preload-copilot-chat events to warm up the chat component without opening it.
  useEffect(() => {
    return subscribePreloadCopilotChat(() => {
      loadCopilotChat()
    })
  }, [loadCopilotChat])

  if (!show) {
    return null
  }

  const shouldUseAgentsHref =
    isFeatureEnabled('copilot_default_to_task_input') &&
    isFeatureEnabled('copilot_redirect_header_button_to_agents') &&
    showAgentsButton

  let analyticsKey = 'COPILOT_CHAT'
  if (isFeatureEnabled('copilot_default_to_task_input')) {
    analyticsKey += '_TASK_BUTTON'
  }
  if (isFeatureEnabled('copilot_default_to_task_input_control_group')) {
    analyticsKey += '_TASK_EXPERIMENT_CONTROL'
  }

  const handleCopilotIconClick = !shouldUseAgentsHref
    ? (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        e.preventDefault()
        loadCopilotChat()
        publishOpenCopilotChat({
          id: 'copilot-assistive',
          intent: CopilotChatIntents.conversation,
          newThread: true,
          source: 'header',
        })
      }
    : undefined

  const copilotItemProps = {
    href: shouldUseAgentsHref ? '/copilot/agents' : '/copilot',
    icon: CopilotIcon,
    'aria-label': shouldUseAgentsHref ? 'Navigate to Copilot' : 'Chat with Copilot',
    analyticsKey,
    analyticsSubCategory: 'COPILOT',
    copilotApiUrl,
    ref: anchorRefCallback,
    onClick: handleCopilotIconClick,
  }

  const shouldShowCopilotChat = anchorReady

  return (
    <div className="hide-sm hide-md">
      <div className={styles.Wrapper}>
        <ButtonGroup>
          <NavLinkItem {...copilotItemProps} />
          {isImmersiveEmbeddedHeaderButton ? (
            <GlobalCopilotMenu
              anchorProps={{
                variant: 'invisible',
                className: clsx(baseStyles.appHeaderButton, styles.CopilotMenu),
                onMouseEnter: loadCopilotChat,
                onFocus: loadCopilotChat,
                onClick: () => {
                  loadCopilotChat()
                  sendItemClick('COPILOT_MENU')
                },
              }}
            />
          ) : showAgentsButton ? (
            <LazyGlobalNavAgentsMenu
              copilotApiUrl={copilotApiUrl}
              anchorProps={{
                variant: 'invisible',
                className: clsx(baseStyles.appHeaderButton, styles.AgentsMenu),
                onClick: () => {
                  sendItemClick('AGENTS_MENU')
                },
              }}
            />
          ) : (
            <GlobalCopilotMenu
              anchorProps={{
                variant: 'invisible',
                className: clsx(baseStyles.appHeaderButton, styles.CopilotMenu),
                onMouseEnter: loadCopilotChat,
                onFocus: loadCopilotChat,
                onClick: () => {
                  loadCopilotChat()
                  sendItemClick('COPILOT_MENU')
                },
              }}
            />
          )}
        </ButtonGroup>
        {isImmersiveEmbeddedHeaderButton && showAgentsButton && (
          <LazyGlobalNavAgentsMenu
            copilotApiUrl={copilotApiUrl}
            anchorProps={{
              variant: 'invisible',
              className: clsx(baseStyles.appHeaderButton, styles.AgentsMenu, styles.AgentsMenuStandalone),
              onClick: () => {
                sendItemClick('AGENTS_MENU')
              },
            }}
          />
        )}
      </div>
      {shouldShowCopilotChat &&
      copilotChatUrl &&
      typeof window !== 'undefined' &&
      (isDeferredPayload || shouldLoadCopilotChat) ? (
        <CopilotChatLoader
          copilotChatUrl={copilotChatUrl}
          pendingOpenEvent={pendingOpenEvent}
          onPendingEventHandled={() => setPendingOpenEvent(null)}
        />
      ) : null}
    </div>
  )
}
